import { getTourV2VariationDuration } from 'lib/tours/tourUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { TOUR_V2_PACKAGE_OPTION_BASE, tourOptionPackages } from 'constants/tours'
import { unique } from 'lib/array/arrayUtils'

export interface TourV2VariationDetails {
  hasExtension: boolean
  isBase: boolean
  countriesCount?: string
  description: string
  durationInDaysCount: string
  endLocation?: string
  hasTheSameStartAndEndLocation: boolean
  placesCount?: string
  startLocation?: string
  maxPax?: number
  minAge?: number
  maxAge?: number
  inclusionItems: Array<App.OfferInclusion>
  itineraryDestinations: Array<string>
  itineraryDestinationsDiff: Array<string>
  availablePackageUpgrades: Array<App.Tours.PackageUpgrade>
}

function extractTourV2VariationDetails(variation: App.Tours.TourV2OfferVariation): TourV2VariationDetails {
  const daysCount = getTourV2VariationDuration(variation)
  const hasExtension = tourOptionPackages.includes(variation.packageOptionName ?? '')
  const isBase = (hasExtension && variation.packageOptionName === TOUR_V2_PACKAGE_OPTION_BASE) || !hasExtension
  const extractedDetails: TourV2VariationDetails = {
    hasExtension,
    isBase,
    description: variation.description,
    startLocation: variation.startLocation,
    endLocation: variation.endLocation,
    hasTheSameStartAndEndLocation: variation.startLocation === variation.endLocation,
    durationInDaysCount: pluralizeToString('day', daysCount),
    maxPax: variation.maxPax,
    minAge: variation.minChildPriceAge,
    maxAge: variation.maxAge,
    inclusionItems: variation.inclusionItems,
    itineraryDestinations: variation.itineraryDestinations,
    itineraryDestinationsDiff: variation.itineraryDestinationsDiff,
    availablePackageUpgrades: variation.availablePackageUpgrades ?? [],
  }

  if (variation.countriesVisited.length) {
    extractedDetails.countriesCount = pluralizeToString('country', variation.countriesVisited.length)
  }
  if (variation.itinerary.length) {
    const uniquePlaces = getTourV2PlacesVisited(variation.itinerary)
    if (uniquePlaces.length) {
      extractedDetails.placesCount = pluralizeToString('place', uniquePlaces.length)
    }
  }

  return extractedDetails
}

export function formatTourV2VariationDetailCounts(variation: App.Tours.TourV2OfferVariation): {
  variationDetailsCountText: string,
} {
  const { countriesCount, durationInDaysCount, placesCount } = extractTourV2VariationDetails(variation)
  const counts = [durationInDaysCount, placesCount, countriesCount].filter(i => !!i)

  return {
    variationDetailsCountText: counts.join(' · '),
  }
}

export function getTourV2PlacesVisited(itinerary: Array<App.Tours.TourV2OfferItineraryItem>): Array<string> {
  return unique(itinerary
    .flatMap(item => item.locationsVisitedDetails)
    .map(location => location.name),
  )
}

export default extractTourV2VariationDetails
